import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  Optional,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { containsExtent } from 'ol/extent';
import { Geometry } from 'ol/geom';
import { takeWhile } from 'rxjs/operators';
import { DynamicFormComponent } from '../../../../../gk-dynamic-form/gk-dynamic-form.component';
import { FieldConfig } from '../../../../../gk-dynamic-form/gk-dynamic-form.model';
import { RangeTypeValue } from '../../../../configs';
import { MapControl } from '../../../../controls';
import {
  FileLoaderFormResult,
  FileLoaderFormValue,
  FileState,
  GeometryFormat,
  MapObject,
  NamesFileLoadFormControls,
  OpenLayersGeometryType,
  SourceType,
  ToolType,
  Wkt,
} from '../../../../models';
import { MapStateService, PolygonTopologyService } from '../../../../services';
import { ConversionUtils, ShapeUtils } from '../../../../utils';
import { TranslateService } from '@ngx-translate/core';
import DxfParser from 'dxf-parser';

@Component({
  selector: 'gk-file-loader-form',
  templateUrl: './file-loader-form.component.html',
  providers: [PolygonTopologyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileLoaderFormComponent
  extends MapControl
  implements AfterViewInit, OnDestroy
{
  protected isAlive = true;
  protected readonly ToolType = ToolType;
  @ViewChild('formContainer')
  formContainer: DynamicFormComponent;
  override toolType: ToolType;
  formValue: FileLoaderFormValue;
  convertedGeometry: Geometry;
  fileList: FileList;
  isValidFormat = false;
  isValidGeometryType = false;
  isValidRange = false;
  isValidTopology = false;
  override sourceType = SourceType.File;
  showFormatTxt: boolean;
  geometryTypesToCheckTopology = [
    OpenLayersGeometryType.Polygon,
    OpenLayersGeometryType.MultiPolygon,
  ];
  convertingGeometryInProgress: boolean;
  wkt: Wkt;
  polygonTopologyService = inject(PolygonTopologyService);
  changeDetectorRef = inject(ChangeDetectorRef);

  constructor(
    @Optional() private activeModal: NgbActiveModal,
    public mapStateService: MapStateService,
    public translateService: TranslateService,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.setFormValueAndSubscribeToChanges();
  }

  setFormValueAndSubscribeToChanges(): void {
    const form = this.formContainer.form;
    this.formValue = form.value;

    form.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((formValue) => {
        this.formValue = formValue;
        this.showFormatTxt =
          formValue[NamesFileLoadFormControls.GeometryFormat].name ===
          GeometryFormat.Txt;
        this.resetGeometryValidationStatuses();
        this.handleCheckGeometry();
      });
  }

  handleCheckGeometry(): void {
    if (this.fileList && this.fileList.length) {
      const reader = new FileReader();
      reader.readAsText(this.fileList[0]);
      reader.onload = () => {
        this.handleGeometryValidation(
          reader.result as string,
          this.formContainer.form.getRawValue().geometryFormat.name,
        );
      };
    }
  }

  closeModal(): void {
    this.activeModal.close({
      mapObjects: [],
      zoomToSelected: false,
      wkt: '',
    } as FileLoaderFormResult);
  }

  loadOnMap(): void {
    this.activeModal.close({
      mapObjects: this.isValid() ? [this.getMapObject()] : [],
      zoomToSelected: true,
      wkt: this.wkt,
    } as FileLoaderFormResult);
  }

  getValidationStatus(validationParam: boolean): string {
    return this.convertingGeometryInProgress
      ? 'fa fa-spinner fa-pulse fa-fw text-primary'
      : validationParam
        ? 'fa fa-check text-success'
        : 'fa fa-times text-danger';
  }

  resetGeometryValidationStatuses(): void {
    this.isValidFormat = false;
    this.isValidGeometryType = false;
    this.isValidRange = false;
    this.isValidTopology = false;
    this.convertedGeometry = undefined;
  }

  handleFileInputChange(event: Event): void {
    this.fileList = (event.target as HTMLInputElement & EventTarget).files;
    if (this.fileList.length) {
      const reader = new FileReader();
      reader.readAsText(this.fileList[0]);
      reader.onload = () => {
        const geomFormat = ConversionUtils.getGeometryFormat(
          reader.result as string,
        );
        this.setValueSelect(geomFormat);
        this.handleGeometryValidation(
          reader.result as string,
          this.formContainer.form.getRawValue().geometryFormat.name,
        );
      };
    }
  }

  setValueSelect(geomFormat: string): void {
    const form = this.formContainer.form;
    switch (geomFormat) {
      case GeometryFormat.Wkt:
        this.showFormatTxt = false;
        form
          .get(NamesFileLoadFormControls.GeometryFormat)
          .setValue({ value: RangeTypeValue.Wkt, name: GeometryFormat.Wkt });

        return;
      case GeometryFormat.Txt:
        this.showFormatTxt = true;
        form
          .get(NamesFileLoadFormControls.GeometryFormat)
          .setValue({ value: RangeTypeValue.Txt, name: GeometryFormat.Txt });

        return;
      default:
        return;
    }
  }

  handleGeometryValidation(
    geometry: string,
    selectedGeomFormat: GeometryFormat,
  ): void {
    this.convertingGeometryInProgress = true;
    this.changeDetectorRef.markForCheck();
    this.resetGeometryValidationStatuses();
    this.handleGeometryFormatValidation(geometry, selectedGeomFormat);
  }

  handleGeometryFormatValidation(
    geometry: string,
    selectedGeomFormat: GeometryFormat,
  ): void {
    switch (selectedGeomFormat) {
      case GeometryFormat.Wkt:
        this.handleWktValidation(geometry);
        return;
      case GeometryFormat.Txt:
        this.handleTxtValidation(geometry);
        return;
      case GeometryFormat.Dxf:
        this.handleDxfValidation(geometry);
        return;
      case GeometryFormat.Gml:
        this.handleGmlValidation(geometry);
        return;
      default:
        return;
    }
  }

  handleWktValidation(wkt: string): void {
    try {
      this.convertedGeometry = ConversionUtils.getGeometryFromWkt(
        wkt.replace(/['"]/g, ''),
      );
      this.wkt = wkt;
      this.geometryConvertSuccessCallback();
    } catch (err) {
      this.convertingGeometryInProgress = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  handleTxtValidation(rangeFile: string): void {
    this.polygonTopologyService
      .convertRangeFileToWkt(rangeFile)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe({
        next: (wkt) => {
          try {
            this.convertedGeometry = ConversionUtils.getGeometryFromWkt(
              wkt.replace(/['"]/g, ''),
            );
            this.wkt = wkt;
            this.geometryConvertSuccessCallback();
          } catch (err) {
            this.convertingGeometryInProgress = false;
            this.changeDetectorRef.markForCheck();
          }
        },
        error: () => {
          this.convertingGeometryInProgress = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  handleDxfValidation(dxfContent: string): void {
    const parser = new DxfParser();
    try {
      const dxf = parser.parseSync(dxfContent);
      this.wkt = ConversionUtils.getWktGeometryFromDxf(dxf);
      this.convertedGeometry = ConversionUtils.getGeometryFromWkt(this.wkt);
      this.dxfConvertSuccessCallback();
    } catch (err) {
      this.convertingGeometryInProgress = false;
      this.isValidFormat = !!this.wkt;
      this.isValidGeometryType = !!this.convertedGeometry;
      this.changeDetectorRef.markForCheck();

      console.error(err);
    }
  }

  handleGmlValidation(gmlContent: string): void {
    try {
      const parser = new DOMParser();
      const gmlDoc = parser.parseFromString(gmlContent, 'application/xml');
      this.wkt = ConversionUtils.getWktGeometryFromGml(gmlDoc);
      this.convertedGeometry = ConversionUtils.getGeometryFromWkt(this.wkt);
      this.gmlConvertSuccessCallback();
    } catch (err) {
      this.convertingGeometryInProgress = false;
      this.isValidFormat = !!this.wkt;
      this.isValidGeometryType = !!this.convertedGeometry;
      this.changeDetectorRef.markForCheck();

      console.error(err);
    }
  }

  dxfConvertSuccessCallback(): void {
    this.convertingGeometryInProgress = false;
    this.isValidFormat = true;
    const geometryType = this.convertedGeometry?.getType();
    this.handleGeometryTypeValidation(geometryType);
    this.handleRangeValidation();
    this.handleTopologyValidation(geometryType);
  }

  gmlConvertSuccessCallback(): void {
    this.convertingGeometryInProgress = false;
    this.isValidFormat = true;
    const geometryType = this.convertedGeometry?.getType();
    this.handleGeometryTypeValidation(geometryType);
    this.handleRangeValidation();
    this.handleTopologyValidation(geometryType);
  }

  geometryConvertSuccessCallback(): void {
    this.convertingGeometryInProgress = false;
    this.isValidFormat = true;
    const geometryType = this.convertedGeometry?.getType();
    this.handleGeometryTypeValidation(geometryType);
    this.handleRangeValidation();
    this.handleTopologyValidation(geometryType);
  }

  handleGeometryTypeValidation(geometryType: any): void {
    if (
      _.includes(
        (this.mapState.toolsState[this.toolType][this.sourceType] as FileState)
          .geometryTypes,
        geometryType,
      )
    ) {
      this.isValidGeometryType = true;
    }
  }

  handleRangeValidation(): void {
    if (this.isGeometryExtentContainedInFullExtent()) {
      this.isValidRange = true;
    }
  }

  isGeometryExtentContainedInFullExtent(): boolean {
    return containsExtent(
      this.mapState.viewState.fullNativeExtent,
      this.convertedGeometry.getExtent(),
    );
  }

  handleTopologyValidation(geometryType: any): void {
    _.includes(this.geometryTypesToCheckTopology, geometryType)
      ? this.checkTopology()
      : (this.isValidTopology = true);
  }

  checkTopology(): void {
    this.polygonTopologyService
      .getPolygonTopologyValidation(
        ConversionUtils.getWktFromGeometry(this.convertedGeometry),
      )
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((validationStatus) => {
        this.isValidTopology = validationStatus.isValid;
        this.changeDetectorRef.markForCheck();
      });
  }

  getMapObject(): MapObject {
    return ShapeUtils.getAnyGeometryMapObject(
      ConversionUtils.getWktFromGeometry(
        ConversionUtils.getReducedGeometry(this.convertedGeometry),
      ),
      this.mapState.toolsState[this.toolType].mapObjectApiType,
    );
  }

  getFileState(): FileState {
    return this.mapState.toolsState[this.toolType][
      this.sourceType
    ] as FileState;
  }

  getFieldConfigs(): FieldConfig[] {
    return this.getFileState().fieldConfigs;
  }

  getFormHeader(): string {
    return this.getFileState().formHeader;
  }

  isValid = (): boolean =>
    this.isValidFormat &&
    this.isValidGeometryType &&
    this.isValidRange &&
    this.isValidTopology;

  getGeometryTypeValidText(): string {
    if (this.isValidGeometryType) {
      return this.translateService.instant('GK.MAP.PROPER_GEOMETRY_TYPE');
    }

    const geometryType = this.convertedGeometry?.getType();
    const requiredGeometryTypes = (
      this.mapState.toolsState[this.toolType][this.sourceType] as FileState
    ).geometryTypes;

    if (!geometryType) {
      return this.translateService.instant('GK.MAP.WRONG_GEOMETRY_TYPE', {
        requiredGeometryType: requiredGeometryTypes[0],
      });
    }

    if (requiredGeometryTypes.length === 1) {
      return this.translateService.instant(
        'GK.MAP.WRONG_GEOMETRY_TYPE_SINGLE',
        {
          geometryType,
          requiredGeometryType: requiredGeometryTypes[0],
        },
      );
    }

    return this.translateService.instant(
      'GK.MAP.WRONG_GEOMETRY_TYPE_WITH_TYPES',
      {
        geometryType,
        requiredGeometryTypes: requiredGeometryTypes.join(', '),
      },
    );
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
